import { type HTMLAttributes } from 'react';
import { Box, Typography } from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import { type Grader } from '../api';

/**
 * Custom filter function for grader autocomplete that matches against
 * both name and email (case-insensitive).
 *
 * Uses MUI's createFilterOptions as a base, but overrides stringification
 * to include both name and email in the searchable text.
 */
export const filterGraderOptions = createFilterOptions<Grader>({
    stringify: (option: Grader) => `${option.name} ${option.email}`,
});

/**
 * Consistent styling for grader options in autocomplete dropdowns.
 * Shows name on first line, email in smaller gray text below.
 */
interface GraderOptionRendererProps {
    props: HTMLAttributes<HTMLLIElement>;
    option: Grader;
}

export function GraderOptionRenderer({ props, option }: GraderOptionRendererProps) {
    const { key, ...otherProps } = props as HTMLAttributes<HTMLLIElement> & { key?: string };

    return (
        <li key={key ?? option.id} {...otherProps}>
            <Box>
                <Typography variant="body2" component="div">
                    {option.name}
                </Typography>
                {option.email && (
                    <Typography variant="caption" component="div" sx={{ color: 'text.secondary' }}>
                        {option.email}
                    </Typography>
                )}
            </Box>
        </li>
    );
}

/**
 * Helper function to create renderOption prop for MUI Autocomplete.
 * Usage: renderOption={(props, option) => renderGraderOption(props, option)}
 */
export function renderGraderOption(props: HTMLAttributes<HTMLLIElement>, option: Grader) {
    return <GraderOptionRenderer props={props} option={option} />;
}

/**
 * Get display label for grader (for input field when selected).
 * Shows name only - the dropdown shows full details.
 */
export function getGraderLabel(grader: Grader): string {
    return grader.name;
}

/**
 * Get full display label including email (for filters and chips).
 */
export function getGraderFullLabel(grader: Grader): string {
    return grader.email ? `${grader.name} (${grader.email})` : grader.name;
}
